"use client";

import { Group, Resource } from "@/db/schema";
import { DialogDescription, DialogTitle, DialogTrigger } from "@radix-ui/react-dialog";
import clsx from "clsx";
import Image from "next/image";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader } from "../ui/dialog";
import { resourceColor } from "@/lib/resourceColor";

export function CalendarTitle({ resource, group }: { resource: Resource; group: Group }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  const dynamicFieldsData =
    resource.dynamicFieldValues && group?.dynamicFields?.length
      ? group.dynamicFields
          .filter((field) => field.target === "resource")
          .map((field) => ({
            name: field.name,
            value: resource.dynamicFieldValues?.[field.id] || "",
          }))
          .filter((field) => field.value) // Only show fields with values
      : [];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div
          className="sticky top-0 bg-white border-b p-2 min-w-[120px] sm:w-auto w-full cursor-pointer hover:opacity-70 transition-opacity"
          style={{
            height: "var(--title-height)",
          }}
        >
          <div className="relative w-full h-full">
            <div className="h-full w-full object-cover rounded-md overflow-hidden flex items-center">
              {resource.imageId && !imageError ? (
                <div
                  className="relative w-full h-full"
                  style={{ backgroundColor: resourceColor(resource.name) }}
                >
                  <Image
                    priority
                    onError={() => {
                      setImageError(true);
                    }}
                    onLoad={() => {
                      setImageLoaded(true);
                    }}
                    src={`https://${process.env.NEXT_PUBLIC_CLOUDFRONT_ID}.cloudfront.net/${resource.imageId}`}
                    alt="Resource background"
                    width={600}
                    height={600}
                    className={clsx(
                      "w-full h-full object-cover transition-opacity",
                      imageLoaded ? "opacity-100" : "opacity-0",
                    )}
                  />
                </div>
              ) : (
                <div
                  style={{ backgroundColor: resourceColor(resource.name) }}
                  className="top-0 left-0 h-full w-full object-cover rounded-md overflow-hidden"
                />
              )}
            </div>
            <div className="mt-auto absolute bottom-0 left-0 w-full p-2">
              <div className="bg-white rounded p-2 text-sm">
                <h2 className="truncate font-semibold">{resource.name}</h2>
                <p className="text-gray-500 text-xs truncate">
                  {resource.capacity ? <span>{resource.capacity} people. </span> : null}
                  {resource.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader className="text-start">
          <DialogTitle className="font-bold">{resource.name}</DialogTitle>
          {resource.capacity ? (
            <DialogDescription className="text-sm">{resource.capacity} people</DialogDescription>
          ) : null}
          {dynamicFieldsData.length
            ? dynamicFieldsData.map((field, index) => (
                <p className="text-sm" key={index}>
                  {field.name}: {field.value}
                </p>
              ))
            : null}
        </DialogHeader>

        <p className="text-gray-500 text-sm">{resource.description}</p>

        <div className="rounded object-cover overflow-hidden">
          {resource.imageId && !imageError ? (
            <Image
              priority
              onError={() => {
                setImageError(true);
              }}
              src={`https://${process.env.NEXT_PUBLIC_CLOUDFRONT_ID}.cloudfront.net/${resource.imageId}`}
              width={400}
              height={400}
              alt="Resource background"
              className="w-full h-full"
            />
          ) : (
            <div className="top-0 left-0 h-full w-full object-cover rounded-md overflow-hidden"></div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
